import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiClient } from '@/hooks/useApiClient';
import { cn } from '@/lib/utils';

import { DropDownList } from '@/components/form/inputs/DropDownList';
import { MachineManagerModal } from '@/features/traceability/components/MachineManagerModal';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { IconButton } from '@/components/ui/icon-button';
import { RefreshCw } from 'lucide-react';

import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import type { Machine } from '@/features/traceability/type/Machine';
import type { Material } from '@/features/traceability/type/Material';
import type { TraceabilityApiClient as TApiClient } from '@/features/traceability/api/TraceabilityApiClient';

import { SectionHeader, FieldRow } from './TraceEditPrimitives';
import { TraceEditSections } from './TraceEditSections';

export type TraceEditBodyHandle = {
  validate: () => boolean;
};

type TraceEditBodyProps = {
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  traceabilityApiClient: TApiClient | null;
  companyName: string;
};

export const TraceEditBody = forwardRef<TraceEditBodyHandle, TraceEditBodyProps>(
  function TraceEditBody(
    { values, onChange, traceabilityApiClient: externalClient, companyName },
    ref
  ) {
    const { t } = useTranslation();
    const http = useApiClient();

    // ── Validation refs ───────────────────────────────────────────────────────
    const operatorRef = useRef<{ validate: () => boolean }>(null);
    const durationTotalRef = useRef<{ validate: () => boolean }>(null);
    const durationPieceRef = useRef<{ validate: () => boolean }>(null);
    const machineRef = useRef<{ validate: () => boolean }>(null);
    const lubTypeRef = useRef<{ validate: () => boolean }>(null);
    const toolStateRef = useRef<{ validate: () => boolean }>(null);
    const toolChangeRef = useRef<{ validate: () => boolean }>(null);

    useImperativeHandle(ref, () => ({
      validate: () => {
        const results = [
          operatorRef.current?.validate() ?? true,
          durationTotalRef.current?.validate() ?? true,
          durationPieceRef.current?.validate() ?? true,
          machineRef.current?.validate() ?? true,
          lubTypeRef.current?.validate() ?? true,
          toolStateRef.current?.validate() ?? true,
          toolChangeRef.current?.validate() ?? true,
        ];
        return results.every(Boolean);
      },
    }));

    // ── Machine state ─────────────────────────────────────────────────────────
    const [machines, setMachines] = useState<Machine[]>([]);
    const [machineLoading, setMachineLoading] = useState(false);
    const [showManager, setShowManager] = useState(false);

    const internalClient = useMemo(() => (http ? new TraceabilityApiClient(http) : null), [http]);
    const apiClient = externalClient ?? internalClient;

    // ── Materials state ───────────────────────────────────────────────────────
    const [materials, setMaterials] = useState<Material[]>([]);

    useEffect(() => {
      if (!apiClient) return;
      const ac = new AbortController();
      apiClient
        .materialReadAll({}, { signal: ac.signal })
        .then((res) => {
          if (ac.signal.aborted) return;
          if (res.ok && res.data?.materials) {
            setMaterials(res.data.materials.map((item: { material: Material }) => item.material));
          }
        })
        .catch((err: unknown) => {
          if (!ac.signal.aborted) console.error(err);
        });
      return () => ac.abort();
    }, [apiClient]);

    const fetchMachines = async () => {
      if (!apiClient) return;
      setMachineLoading(true);
      try {
        const res = await apiClient.machineReadAll({});
        if (res.ok && res.data?.machines) {
          setMachines(res.data.machines.map((item: { machine: Machine }) => item.machine));
        }
      } catch (err) {
        console.error('Failed to fetch machines:', err);
      } finally {
        setMachineLoading(false);
      }
    };

    useEffect(() => {
      if (!apiClient) return;
      const ac = new AbortController();
      setMachineLoading(true);
      apiClient
        .machineReadAll({}, { signal: ac.signal })
        .then((res) => {
          if (ac.signal.aborted) return;
          if (res.ok && res.data?.machines) {
            setMachines(res.data.machines.map((item: { machine: Machine }) => item.machine));
          }
        })
        .catch((err: unknown) => {
          if (!ac.signal.aborted) console.error(err);
        })
        .finally(() => {
          if (!ac.signal.aborted) setMachineLoading(false);
        });
      return () => ac.abort();
    }, [apiClient]);

    const selectedMachineName = (values['machineName'] as string) ?? '';
    const selectedMachineId = values['machineId'] != null ? Number(values['machineId']) : null;
    const selectedMachine = useMemo(
      () =>
        machines.find((m) => selectedMachineId != null && m.id === selectedMachineId) ??
        machines.find((m) => m.name === selectedMachineName) ??
        null,
      [machines, selectedMachineId, selectedMachineName]
    );

    const handleSelectMachine = (val: string | null) => {
      if (!val) return;
      const asId = Number(val);
      const m =
        !isNaN(asId) && asId > 0
          ? machines.find((m) => m.id === asId)
          : machines.find((m) => m.name === val);
      if (m) {
        onChange('machineName', m.name);
        onChange('machineBrand', m.brand);
        onChange('machineModel', m.model);
        onChange('machineSerial', m.serialNumber);
        onChange('machineCompanyName', m.companyName);
        onChange('machineKafkaName', m.kafkaName ?? '');
        onChange('machineId', m.id ?? null);
        // Keep the carried machine object in sync — the save path must never
        // fall back to the stale seeded machine.
        onChange('machine', m);
      }
    };

    const selectedDropdownValue = selectedMachine
      ? selectedMachine.id !== undefined
        ? String(selectedMachine.id)
        : selectedMachine.name
      : selectedMachineName;

    const sectionRefs = {
      operatorRef,
      durationTotalRef,
      durationPieceRef,
      lubTypeRef,
      toolStateRef,
      toolChangeRef,
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
      <div className="divide-y divide-border/30">
        <TraceEditSections
          values={values}
          onChange={onChange}
          refs={sectionRefs}
          materials={materials}
          traceabilityApiClient={apiClient}
        />

        {/* Machine */}
        <div>
          <SectionHeader title={t('toolView.advanced.sections.machine')} />
          <FieldRow>
            <div className="flex gap-2">
              <div className="flex-1">
                <DropDownList
                  ref={machineRef}
                  name="machineName"
                  label={t('traceability.machineTab.dropdownLabel')}
                  value={selectedDropdownValue}
                  onChange={handleSelectMachine}
                  options={machines.map((m) => ({
                    label: m.name,
                    value: m.id !== undefined ? String(m.id) : m.name,
                    id: m.id,
                  }))}
                  placeholder={t('traceability.machineTab.dropdownPlaceholder')}
                  required
                />
              </div>
              <div className="flex items-end pb-0.5">
                <IconButton
                  icon={RefreshCw}
                  size="icon"
                  onClick={() => void fetchMachines()}
                  loading={machineLoading}
                  label={t('traceability.machineTab.refreshList')}
                />
              </div>
            </div>
            {selectedMachine && (
              <Card
                as="dl"
                tier="muted"
                padding="none"
                className="mt-3 flex flex-wrap items-center gap-0 px-4 py-2.5"
              >
                <div className="flex flex-col border-r border-border/60 pr-4 mr-4">
                  <dt className="text-xs text-muted-foreground">
                    {t('traceability.machineTab.brandLabel')}
                  </dt>
                  <dd className="text-sm font-medium">{selectedMachine.brand}</dd>
                </div>
                <div className="flex flex-col border-r border-border/60 pr-4 mr-4">
                  <dt className="text-xs text-muted-foreground">
                    {t('traceability.machineTab.modelLabel')}
                  </dt>
                  <dd className="text-sm font-medium">{selectedMachine.model}</dd>
                </div>
                <div
                  className={cn(
                    'flex flex-col',
                    selectedMachine.kafkaName && 'border-r border-border/60 pr-4 mr-4'
                  )}
                >
                  <dt className="text-xs text-muted-foreground">
                    {t('traceability.machineTab.serialLabel')}
                  </dt>
                  <dd className="font-mono text-sm">{selectedMachine.serialNumber}</dd>
                </div>
                {selectedMachine.kafkaName && (
                  <div className="flex flex-col">
                    <dt className="text-xs text-muted-foreground">
                      {t('traceability.machineTab.kafkaNameLabel')}
                    </dt>
                    <dd className="font-mono text-sm">{selectedMachine.kafkaName}</dd>
                  </div>
                )}
              </Card>
            )}
            <div className="mt-3">
              <Button
                type="button"
                variant="link"
                size="inline"
                onClick={() => setShowManager(true)}
              >
                {t('traceability.machineTab.openManager')}
              </Button>
            </div>
          </FieldRow>
          <MachineManagerModal
            isOpen={showManager}
            companyName={companyName}
            onClose={() => setShowManager(false)}
            onSave={async (machine, action) => {
              await fetchMachines();
              if (machine && action !== 'delete')
                handleSelectMachine(machine.id !== undefined ? String(machine.id) : machine.name);
              if (
                machine &&
                action === 'delete' &&
                (machine.id !== undefined
                  ? machine.id === selectedMachine?.id
                  : machine.name === selectedMachineName)
              ) {
                onChange('machineName', '');
                onChange('machineBrand', '');
                onChange('machineModel', '');
                onChange('machineSerial', '');
                onChange('machineKafkaName', '');
              }
            }}
            selectedMachineName={selectedMachineName}
          />
        </div>
      </div>
    );
  }
);
